/**
 * AccountProvisioning - User account provisioning for Ultra/Enterprise users
 * Allows creating and managing user accounts for their organization.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  UserPlus,
  Upload,
  Search,
  MoreVertical,
  Edit3,
  RefreshCw,
  Key,
  Trash2,
  Download,
  AlertTriangle,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Copy,
  Loader2,
  Users,
  Coins,
  BarChart3,
  TrendingUp,
  Image,
  Video,
  Trophy,
  Calendar
} from "lucide-react";
import PropTypes from "prop-types";
import { getStoredTokens } from "../api/client";

// Use environment variable for API URL; fallback to localhost for local development
const API_BASE = process.env.REACT_APP_API_URL || (
  window.location.hostname === 'localhost' ? 'http://localhost:8000' : 'https://api.cloudmortgage.io'
);

// Generate a random password
const generatePassword = (length = 12) => {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

// Parse CSV content
const parseCSV = (content) => {
  const lines = content.trim().split("\n");
  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
  const accounts = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",").map(v => v.trim());
    if (values.length >= 4) {
      accounts.push({
        email: values[headers.indexOf("email")] || values[0],
        password: values[headers.indexOf("password")] || values[1],
        first_name: values[headers.indexOf("first_name")] || values[2],
        last_name: values[headers.indexOf("last_name")] || values[3],
        tier: values[headers.indexOf("tier")] || values[4] || "rp_starter",
        role: values[headers.indexOf("role")] || values[5] || "Lender",
        credits: values[headers.indexOf("credits")] ? parseInt(values[headers.indexOf("credits")]) : null
      });
    }
  }
  return accounts;
};

// Create Account Modal
const CreateAccountModal = ({ isOpen, onClose, onSubmit, config, isLoading }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
    tier: "rp_starter",
    role: "Lender",
    credits: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormData(prev => ({ ...prev, password: newPassword, confirmPassword: newPassword }));
    setShowPassword(true);
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(formData.password);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Invalid email format";
    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 6) newErrors.password = "Password must be at least 6 characters";
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    if (!formData.first_name) newErrors.first_name = "First name is required";
    if (!formData.last_name) newErrors.last_name = "Last name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        tier: formData.tier,
        role: formData.role,
        credits: formData.credits ? parseInt(formData.credits) : null
      });
    }
  };

  if (!isOpen) return null;

  const selectedTier = config?.tiers?.find(t => t.value === formData.tier);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-emerald-500" />
            Create New Account
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className={`w-full px-4 py-2 rounded-lg border ${errors.email ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'} bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent`}
              placeholder="user@example.com"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Password <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className={`w-full px-4 py-2 pr-20 rounded-lg border ${errors.password ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'} bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent`}
                  placeholder="Min 6 characters"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-1 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  {formData.password && (
                    <button
                      type="button"
                      onClick={handleCopyPassword}
                      className="p-1 text-slate-400 hover:text-slate-600"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="px-3 py-2 bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-500 text-sm font-medium"
              >
                Generate
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              className={`w-full px-4 py-2 rounded-lg border ${errors.confirmPassword ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'} bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent`}
            />
            {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                className={`w-full px-4 py-2 rounded-lg border ${errors.first_name ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'} bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent`}
              />
              {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                className={`w-full px-4 py-2 rounded-lg border ${errors.last_name ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'} bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent`}
              />
              {errors.last_name && <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>}
            </div>
          </div>

          {/* Tier */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Subscription Tier
            </label>
            <select
              value={formData.tier}
              onChange={(e) => setFormData(prev => ({ ...prev, tier: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              {config?.tiers?.map(tier => (
                <option key={tier.value} value={tier.value}>
                  {tier.label} ({tier.credits?.toLocaleString()} credits/mo)
                </option>
              ))}
            </select>
            {selectedTier?.description && (
              <p className="text-xs text-slate-500 mt-1">{selectedTier.description}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              {config?.roles?.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>

          {/* Credits Override */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Initial Credits <span className="text-slate-400">(optional)</span>
            </label>
            <input
              type="number"
              value={formData.credits}
              onChange={(e) => setFormData(prev => ({ ...prev, credits: e.target.value }))}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder={`Default: ${selectedTier?.credits?.toLocaleString() || 'tier default'}`}
              min="0"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Create Account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

CreateAccountModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  config: PropTypes.object,
  isLoading: PropTypes.bool
};

// Bulk Import Modal
const BulkImportModal = ({ isOpen, onClose, onSubmit, config, isLoading }) => {
  const [mode, setMode] = useState("csv"); // csv | manual
  const [csvContent, setCsvContent] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [parseError, setParseError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result;
        setCsvContent(content);
        try {
          const parsed = parseCSV(content);
          setAccounts(parsed);
          setParseError(null);
        } catch (err) {
          setParseError("Failed to parse CSV file");
        }
      };
      reader.readAsText(file);
    }
  };

  const downloadTemplate = () => {
    const template = "email,password,first_name,last_name,tier,role,credits\njohn@example.com,Pass123!,John,Doe,rp_starter,Lender,\njane@example.com,Pass456!,Jane,Smith,rp_pro,Realtor,25000";
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "account_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = () => {
    if (accounts.length > 0) {
      onSubmit(accounts);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-500" />
            Bulk Import Accounts
          </h2>
        </div>

        <div className="p-6 space-y-4">
          {/* Mode Toggle */}
          <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-700 rounded-lg">
            <button
              onClick={() => setMode("csv")}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === "csv" ? "bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow" : "text-slate-600 dark:text-slate-400"}`}
            >
              CSV Upload
            </button>
            <button
              onClick={() => setMode("manual")}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === "manual" ? "bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow" : "text-slate-600 dark:text-slate-400"}`}
            >
              Manual Entry
            </button>
          </div>

          {mode === "csv" && (
            <>
              {/* Download Template */}
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                <Download className="w-4 h-4" />
                Download CSV Template
              </button>

              {/* File Upload */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
              >
                <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                <p className="text-slate-600 dark:text-slate-400">
                  Click to upload CSV file or drag and drop
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>

              {parseError && (
                <p className="text-red-500 text-sm">{parseError}</p>
              )}

              {accounts.length > 0 && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
                  <p className="text-emerald-700 dark:text-emerald-400 font-medium flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    {accounts.length} accounts ready to import
                  </p>
                  <ul className="mt-2 text-sm text-emerald-600 dark:text-emerald-500 space-y-1">
                    {accounts.slice(0, 5).map((acc, i) => (
                      <li key={i}>{acc.email} ({acc.tier})</li>
                    ))}
                    {accounts.length > 5 && (
                      <li>... and {accounts.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
            </>
          )}

          {mode === "manual" && (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <p>Manual entry form coming soon.</p>
              <p className="text-sm mt-1">Please use CSV upload for now.</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={accounts.length === 0 || isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import {accounts.length} Account{accounts.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

BulkImportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  config: PropTypes.object,
  isLoading: PropTypes.bool
};

// Actions Dropdown
const ActionsDropdown = ({ account, onEdit, onResetCredits, onResetPassword, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
      >
        <MoreVertical className="w-4 h-4 text-slate-500" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 py-1">
          <button
            onClick={() => { onEdit(account); setIsOpen(false); }}
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300"
          >
            <Edit3 className="w-4 h-4" /> Edit Account
          </button>
          <button
            onClick={() => { onResetCredits(account); setIsOpen(false); }}
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300"
          >
            <RefreshCw className="w-4 h-4" /> Reset Credits
          </button>
          <button
            onClick={() => { onResetPassword(account); setIsOpen(false); }}
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300"
          >
            <Key className="w-4 h-4" /> Reset Password
          </button>
          <hr className="my-1 border-slate-200 dark:border-slate-700" />
          <button
            onClick={() => { onDelete(account); setIsOpen(false); }}
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
          >
            <Trash2 className="w-4 h-4" /> Delete Account
          </button>
        </div>
      )}
    </div>
  );
};

ActionsDropdown.propTypes = {
  account: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onResetCredits: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ account, onConfirm, onCancel, isLoading }) => {
  if (!account) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Delete Account</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">{account.email}</p>
          </div>
        </div>

        <p className="text-slate-600 dark:text-slate-300 mb-6">
          Are you sure you want to delete this account? This action cannot be undone. The user will no longer be able to log in.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

DeleteConfirmModal.propTypes = {
  account: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

// Analytics Stats Card Component
const AnalyticsStatCard = ({ icon: Icon, label, value, subtext, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    violet: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
          {subtext && <p className="text-xs text-slate-400 dark:text-slate-500">{subtext}</p>}
        </div>
      </div>
    </div>
  );
};

AnalyticsStatCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  subtext: PropTypes.string,
  color: PropTypes.string
};

// Main Component
const AccountProvisioning = ({ userEmail }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState("accounts"); // "accounts" | "analytics"

  // Accounts state
  const [accounts, setAccounts] = useState([]);
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const pageSize = 50;

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [topUsers, setTopUsers] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30); // 7, 14, 30, 90, or null for all time
  const [leaderboardSort, setLeaderboardSort] = useState("total"); // "total", "images", "videos"

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [deleteAccount, setDeleteAccount] = useState(null);

  const getAuthToken = () => {
    const tokens = getStoredTokens();
    return tokens?.accessToken || null;
  };

  // Fetch config
  const fetchConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/provisioning/config`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (err) {
      console.error("Failed to fetch config:", err);
    }
  }, []);

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${API_BASE}/api/provisioning/accounts?page=${page}&page_size=${pageSize}`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
        setTotalAccounts(data.total || 0);
        setHasMore(data.has_more || false);
      } else if (response.status === 403) {
        setError("Account Provisioning is only available for Ultra and Enterprise tiers.");
      } else {
        throw new Error("Failed to fetch accounts");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchConfig();
    fetchAccounts();
  }, [fetchConfig, fetchAccounts]);

  // Fetch analytics summary
  const fetchAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const daysParam = analyticsPeriod ? `?days=${analyticsPeriod}` : '';
      const response = await fetch(
        `${API_BASE}/api/provisioning/analytics/summary${daysParam}`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      }
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [analyticsPeriod]);

  // Fetch top users leaderboard
  const fetchTopUsers = useCallback(async () => {
    try {
      const daysParam = analyticsPeriod ? `days=${analyticsPeriod}` : '';
      const response = await fetch(
        `${API_BASE}/api/provisioning/analytics/top-users?${daysParam}&limit=5&sort_by=${leaderboardSort}`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setTopUsers(data);
      }
    } catch (err) {
      console.error("Failed to fetch top users:", err);
    }
  }, [analyticsPeriod, leaderboardSort]);

  // Fetch analytics when tab changes or period changes
  useEffect(() => {
    if (activeTab === "analytics") {
      fetchAnalytics();
      fetchTopUsers();
    }
  }, [activeTab, fetchAnalytics, fetchTopUsers]);

  // Create single account
  const handleCreateAccount = async (accountData) => {
    try {
      setIsSubmitting(true);
      const response = await fetch(`${API_BASE}/api/provisioning/accounts`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(accountData)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(`Account created successfully for ${accountData.email}`);
        setShowCreateModal(false);
        fetchAccounts();
      } else {
        setError(data.detail || "Failed to create account");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bulk create accounts
  const handleBulkCreate = async (accountsData) => {
    try {
      setIsSubmitting(true);
      const response = await fetch(`${API_BASE}/api/provisioning/accounts/bulk`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ accounts: accountsData })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(`${data.success_count} of ${data.total} accounts created successfully`);
        setShowBulkModal(false);
        fetchAccounts();
      } else {
        setError(data.detail || "Bulk import failed");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    if (!deleteAccount) return;

    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${API_BASE}/api/provisioning/accounts/${encodeURIComponent(deleteAccount.email)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        }
      );

      if (response.ok) {
        setSuccessMessage(`Account ${deleteAccount.email} deleted successfully`);
        setDeleteAccount(null);
        fetchAccounts();
      } else {
        const data = await response.json();
        setError(data.detail || "Failed to delete account");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Filter accounts by search
  const filteredAccounts = accounts.filter(acc =>
    acc.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${acc.first_name} ${acc.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(totalAccounts / pageSize);

  return (
    <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Users className="w-7 h-7 text-emerald-500" />
            Account Provisioning
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Create and manage user accounts for your organization
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab("accounts")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === "accounts"
                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <Users className="w-4 h-4" />
            Accounts
          </button>
          <button
            onClick={() => setActiveTab("analytics")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === "analytics"
                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </button>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-4 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
            <Check className="w-5 h-5" />
            {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertTriangle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* ACCOUNTS TAB */}
        {activeTab === "accounts" && (
          <>
            {/* Actions Bar */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 font-medium flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Create Account
              </button>
              <button
                onClick={() => setShowBulkModal(true)}
                className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 font-medium flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Bulk Import
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search accounts..."
                className="pl-10 pr-4 py-2 w-full sm:w-64 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Accounts Table */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">
                {searchTerm ? "No accounts match your search" : "No provisioned accounts yet"}
              </p>
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                Click "Create Account" to get started
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-800/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Tier</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Credits</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredAccounts.map((account) => (
                      <tr key={account.email} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-6 py-4 text-sm text-slate-900 dark:text-white font-medium">
                          {account.email}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                          {account.first_name} {account.last_name}
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                            {account.tier}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                          {account.role}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                          <span className="flex items-center gap-1">
                            <Coins className="w-3.5 h-3.5 text-amber-500" />
                            {account.credits?.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            account.status === 'active'
                              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                          }`}>
                            {account.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                          {new Date(account.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ActionsDropdown
                            account={account}
                            onEdit={() => {/* TODO: implement edit */}}
                            onResetCredits={() => {/* TODO: implement reset credits */}}
                            onResetPassword={() => {/* TODO: implement reset password */}}
                            onDelete={setDeleteAccount}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalAccounts)} of {totalAccounts} accounts
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 rounded-lg border border-slate-300 dark:border-slate-600 disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={!hasMore}
                      className="p-2 rounded-lg border border-slate-300 dark:border-slate-600 disabled:opacity-50 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
          </>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === "analytics" && (
          <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Usage Analytics</h2>
              <div className="flex gap-1 p-1 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                {[
                  { value: 1, label: "Today" },
                  { value: 7, label: "7d" },
                  { value: 14, label: "14d" },
                  { value: 30, label: "30d" },
                  { value: 90, label: "90d" },
                  { value: null, label: "All" }
                ].map((period) => (
                  <button
                    key={period.label}
                    onClick={() => setAnalyticsPeriod(period.value)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      analyticsPeriod === period.value
                        ? "bg-emerald-500 text-white"
                        : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>

            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
              </div>
            ) : analyticsData ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <AnalyticsStatCard
                    icon={Users}
                    label="Total Users"
                    value={analyticsData.totalUsers}
                    color="blue"
                  />
                  <AnalyticsStatCard
                    icon={Image}
                    label="Images Generated"
                    value={analyticsData.totalImages}
                    color="emerald"
                  />
                  <AnalyticsStatCard
                    icon={Video}
                    label="Videos Generated"
                    value={analyticsData.totalVideos}
                    color="violet"
                  />
                  <AnalyticsStatCard
                    icon={TrendingUp}
                    label="Total Generated"
                    value={analyticsData.totalGenerated}
                    subtext={analyticsPeriod === 1 ? "Today" : analyticsPeriod ? `Last ${analyticsPeriod} days` : "All time"}
                    color="amber"
                  />
                </div>

                {/* Top Performers & User Breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Top Performers */}
                  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-amber-500" />
                        Top Performers
                      </h3>
                      <select
                        value={leaderboardSort}
                        onChange={(e) => setLeaderboardSort(e.target.value)}
                        className="text-sm bg-slate-100 dark:bg-slate-800 border-0 rounded-lg px-3 py-1.5 text-slate-700 dark:text-slate-300"
                      >
                        <option value="total">Total</option>
                        <option value="images">Images</option>
                        <option value="videos">Videos</option>
                      </select>
                    </div>
                    {topUsers?.topUsers?.length > 0 ? (
                      <div className="space-y-3">
                        {topUsers.topUsers.map((user, index) => (
                          <div key={user.email} className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? "bg-amber-100 text-amber-700" :
                              index === 1 ? "bg-slate-200 text-slate-700" :
                              index === 2 ? "bg-orange-100 text-orange-700" :
                              "bg-slate-100 text-slate-500"
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                {user.name || user.email}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {leaderboardSort === "images" ? `${user.imagesGenerated} images` :
                                 leaderboardSort === "videos" ? `${user.videosGenerated} videos` :
                                 `${user.total} total`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
                        No activity yet
                      </p>
                    )}
                  </div>

                  {/* User Breakdown Table */}
                  <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                    <div className="p-5 border-b border-slate-200 dark:border-slate-800">
                      <h3 className="font-semibold text-slate-900 dark:text-white">User Breakdown</h3>
                    </div>
                    {analyticsData.users?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 dark:bg-slate-800/50">
                            <tr>
                              <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">User</th>
                              <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Tier</th>
                              <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Images</th>
                              <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Videos</th>
                              <th className="px-5 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {analyticsData.users.map((user) => (
                              <tr key={user.email} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                <td className="px-5 py-3">
                                  <p className="text-sm font-medium text-slate-900 dark:text-white">{user.name || "—"}</p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
                                </td>
                                <td className="px-5 py-3">
                                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                                    {user.tier}
                                  </span>
                                </td>
                                <td className="px-5 py-3 text-right text-sm text-slate-600 dark:text-slate-300">
                                  {user.imagesGenerated.toLocaleString()}
                                </td>
                                <td className="px-5 py-3 text-right text-sm text-slate-600 dark:text-slate-300">
                                  {user.videosGenerated.toLocaleString()}
                                </td>
                                <td className="px-5 py-3 text-right text-sm font-medium text-slate-900 dark:text-white">
                                  {user.total.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <BarChart3 className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-500 dark:text-slate-400">No usage data yet</p>
                        <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                          Analytics will appear once your users start generating content
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                <BarChart3 className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-400">No analytics data available</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateAccountModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateAccount}
        config={config}
        isLoading={isSubmitting}
      />

      <BulkImportModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSubmit={handleBulkCreate}
        config={config}
        isLoading={isSubmitting}
      />

      <DeleteConfirmModal
        account={deleteAccount}
        onConfirm={handleDeleteAccount}
        onCancel={() => setDeleteAccount(null)}
        isLoading={isSubmitting}
      />
    </div>
  );
};

AccountProvisioning.propTypes = {
  userEmail: PropTypes.string
};

export default AccountProvisioning;
